import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PaintBrush, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandingPage() {
  const [config, setConfig] = useState({ logo_url: '', primary_color: '#7d64ff', company_name: '', favicon_url: '', footer_text: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchBranding(); }, []);

  const fetchBranding = async () => {
    try {
      const { data } = await axios.get(`${API}/api/branding`, { withCredentials: true });
      setConfig(c => ({ ...c, ...data }));
    } catch { /* defaults */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/branding`, config, { withCredentials: true });
      toast.success('Branding updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout><div className="p-6 text-center text-[#9c9db7]">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl" data-testid="branding-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">White-Label Branding</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Customize marketplace appearance</p>
          </div>
          <Button data-testid="save-branding-btn" onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <FloppyDisk size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <Label className="text-sm font-semibold">Company Name</Label>
            <Input data-testid="brand-name" value={config.company_name} onChange={e => setConfig(c => ({ ...c, company_name: e.target.value }))} placeholder="Your Marketplace" className="mt-2" />
          </div>

          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <Label className="text-sm font-semibold">Primary Color</Label>
            <div className="flex items-center gap-3 mt-2">
              <input type="color" value={config.primary_color} onChange={e => setConfig(c => ({ ...c, primary_color: e.target.value }))} className="w-10 h-10 rounded-lg border cursor-pointer" data-testid="brand-color" />
              <Input value={config.primary_color} onChange={e => setConfig(c => ({ ...c, primary_color: e.target.value }))} className="max-w-[150px] font-mono" />
              <div className="w-24 h-10 rounded-lg" style={{ backgroundColor: config.primary_color }} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <Label className="text-sm font-semibold">Logo URL</Label>
            <Input data-testid="brand-logo" value={config.logo_url} onChange={e => setConfig(c => ({ ...c, logo_url: e.target.value }))} placeholder="https://..." className="mt-2" />
            {config.logo_url && <img src={config.logo_url} alt="Logo" className="h-10 mt-3 object-contain" />}
          </div>

          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <Label className="text-sm font-semibold">Favicon URL</Label>
            <Input value={config.favicon_url} onChange={e => setConfig(c => ({ ...c, favicon_url: e.target.value }))} placeholder="https://..." className="mt-2" />
          </div>

          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <Label className="text-sm font-semibold">Footer Text</Label>
            <Input value={config.footer_text} onChange={e => setConfig(c => ({ ...c, footer_text: e.target.value }))} placeholder="© 2026 Your Company" className="mt-2" />
          </div>

          <div className="bg-[#f9f9fb] rounded-xl border border-[#e5e5ea] p-5">
            <p className="text-sm font-semibold text-[#0A0A0A] mb-3">Preview</p>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3 mb-4">
                {config.logo_url ? <img src={config.logo_url} alt="" className="h-8" /> : <PaintBrush size={24} style={{ color: config.primary_color }} />}
                <span className="font-bold text-lg" style={{ color: config.primary_color }}>{config.company_name || 'Your Marketplace'}</span>
              </div>
              <div className="h-8 rounded-lg" style={{ backgroundColor: config.primary_color }} />
              {config.footer_text && <p className="text-xs text-[#9c9db7] mt-3 text-center">{config.footer_text}</p>}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
